import Button from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useDeleteThemeMutation } from "@/shared/services";
import type { TDesignSystem } from "@/shared/types";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";

const DIALOG_ID = "delete-theme-dialog";

interface IProps {
  theme: TDesignSystem;
}

export const DeleteThemeDialog = ({ theme }: IProps) => {
  const dispatch = useAppDispatch();
  const { mutateAsync: deleteTheme, isPending: isDeleting } =
    useDeleteThemeMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleDelete = async () => {
    if (isDeleting) return;

    try {
      await deleteTheme(theme._id);
      toast.success("Theme deleted successfully");
      handleClose();
    } catch {
      // Keep the dialog open so the user can retry without re-navigating.
      toast.error("Failed to delete theme");
    }
  };

  return (
    <Card className="tw-gap-0">
      <CardHeader>
        <CardTitle>Delete {theme.title} theme?</CardTitle>
        <CardAction>
          <CardCloseButton onClick={handleClose} />
        </CardAction>
      </CardHeader>
      <CardContent>
        <p className="tw-font-sans tw-text-sm tw-text-blue-700 tw-mb-8">
          This action can't be undone. Are you sure you want to delete it?
        </p>
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-3">
        <Button variant="secondary" onClick={handleClose}>
          No, keep it
        </Button>
        <Button
          onClick={handleDelete}
          className={cn(
            "tw-bg-[#DC2626] hover:tw-bg-[#b92d3e]",
            isDeleting && "tw-cursor-not-allowed",
          )}
        >
          {isDeleting ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

DeleteThemeDialog.dialogId = DIALOG_ID;
